import { useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { apiClient } from '@/services/api';
import type { BookingSectionInfo } from '@/types/api';
import { Plus, Edit2, Trash2, X } from 'lucide-react';
import { useHasPermission } from '@/hooks/usePermission';
import { PERM } from '@/constants/permissions';
import { getSyncStatusBadge } from '@/utils/syncStatus';

interface SectionsTabProps {
  restaurantId: number;
}

export function SectionsTab({ restaurantId }: SectionsTabProps) {
  const queryClient = useQueryClient();

  const canCreate = useHasPermission(PERM.BOOKING_CREATE);
  const canUpdate = useHasPermission(PERM.BOOKING_UPDATE);
  const canDelete = useHasPermission(PERM.BOOKING_DELETE);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSection, setEditingSection] = useState<BookingSectionInfo | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Fetch sections
  const { data: sections = [], isLoading } = useQuery({
    queryKey: ['booking-sections', restaurantId],
    queryFn: () => apiClient.getBookingSections(restaurantId),
    enabled: !!restaurantId,
  });

  // Delete mutation — uuid is the cross-table identifier.
  const deleteMutation = useMutation({
    mutationFn: (uuid: string) => apiClient.deleteBookingSection(restaurantId, uuid),
    onSuccess: () => {
      setDeleteError(null);
      queryClient.invalidateQueries({ queryKey: ['booking-sections', restaurantId] });
    },
    onError: (error: any) => {
      setDeleteError(error.response?.data?.message || 'Failed to delete section');
    },
  });

  const handleDelete = async (uuid: string | undefined) => {
    if (!uuid) return;
    if (window.confirm('Are you sure you want to delete this section?')) {
      try {
        await deleteMutation.mutateAsync(uuid);
      } catch {
        // surfaced via deleteMutation's onError -> deleteError banner
      }
    }
  };

  const handleEdit = (section: BookingSectionInfo) => {
    setEditingSection(section);
    setIsModalOpen(true);
  };

  const handleCreate = () => {
    setEditingSection(null);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingSection(null);
  };

  const handleModalSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['booking-sections', restaurantId] });
    setIsModalOpen(false);
    setEditingSection(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading sections...</div>
      </div>
    );
  }

  return (
    <>
      {deleteError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm flex items-center justify-between">
          <span>{deleteError}</span>
          <button onClick={() => setDeleteError(null)} className="text-red-700 hover:text-red-900 font-medium ml-4">
            Dismiss
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600">
          {sections.length} {sections.length === 1 ? 'section' : 'sections'}
        </div>
        {canCreate && (
          <button onClick={handleCreate} className="btn btn-primary h-10">
            <Plus className="h-4 w-4 mr-2" />
            Add Section
          </button>
        )}
      </div>

      {/* Table */}
      {sections.length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-gray-500">No booking sections configured yet.</p>
          <p className="text-sm text-gray-400 mt-1">
            Add a section to organize booking areas for this restaurant.
          </p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Duration (min)
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Max Guests
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Max Bookings/Slot
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Color
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Enabled
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sort Order
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sync Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sections.map((section) => {
                  const badge = getSyncStatusBadge(section.syncStatus);
                  return (
                    <tr key={section.uuid ?? section.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {section.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {section.defaultDurationMinutes}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {section.maxGuests ?? '\u2014'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {section.maxBookingsPerSlot ?? '\u2014'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {section.color ? (
                          <div className="flex items-center space-x-2">
                            <span
                              className="inline-block w-5 h-5 rounded border border-gray-300"
                              style={{ backgroundColor: section.color }}
                            />
                            <span>{section.color}</span>
                          </div>
                        ) : (
                          '\u2014'
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            section.enabled
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {section.enabled ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {section.sortOrder}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.className}`}
                        >
                          {badge.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end space-x-1">
                          {canUpdate && (
                            <button
                              onClick={() => handleEdit(section)}
                              className="text-primary-600 hover:text-primary-900 hover:bg-primary-50 rounded-md p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
                              aria-label={`Edit section ${section.name}`}
                            >
                              <Edit2 className="h-5 w-5" />
                            </button>
                          )}
                          {canDelete && (
                            <button
                              onClick={() => handleDelete(section.uuid as unknown as string)}
                              className="text-red-600 hover:text-red-900 hover:bg-red-50 rounded-md p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
                              aria-label={`Delete section ${section.name}`}
                            >
                              <Trash2 className="h-5 w-5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <SectionModal
          restaurantId={restaurantId}
          section={editingSection}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}
    </>
  );
}

// Section Modal Component for Create/Edit
interface SectionModalProps {
  restaurantId: number;
  section: BookingSectionInfo | null;
  onClose: () => void;
  onSuccess: () => void;
}

function SectionModal({ restaurantId, section, onClose, onSuccess }: SectionModalProps) {
  const [formData, setFormData] = useState({
    name: section?.name || '',
    defaultDurationMinutes: section?.defaultDurationMinutes ?? 60,
    sortOrder: section?.sortOrder ?? 0,
    enabled: section?.enabled ?? true,
    color: section?.color || '',
    maxGuests: section?.maxGuests ?? ('' as number | ''),
    maxBookingsPerSlot: section?.maxBookingsPerSlot ?? ('' as number | ''),
    lastBookingMinutesBeforeClose: section?.lastBookingMinutesBeforeClose ?? ('' as number | ''),
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const createMutation = useMutation({
    mutationFn: (data: Partial<BookingSectionInfo>) =>
      apiClient.createBookingSection(restaurantId, data),
    onSuccess,
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to create section';
      setErrors({ submit: message });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: Partial<BookingSectionInfo>) =>
      apiClient.updateBookingSection(restaurantId, section!.uuid as unknown as string, data),
    onSuccess,
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to update section';
      setErrors({ submit: message });
    },
  });

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Section name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    const submitData: Partial<BookingSectionInfo> = {
      name: formData.name,
      defaultDurationMinutes: formData.defaultDurationMinutes,
      sortOrder: formData.sortOrder,
      enabled: formData.enabled,
      color: formData.color || undefined,
      maxGuests: formData.maxGuests !== '' ? Number(formData.maxGuests) : undefined,
      maxBookingsPerSlot: formData.maxBookingsPerSlot !== '' ? Number(formData.maxBookingsPerSlot) : undefined,
      lastBookingMinutesBeforeClose:
        formData.lastBookingMinutesBeforeClose !== ''
          ? Number(formData.lastBookingMinutesBeforeClose)
          : undefined,
    };

    if (section) {
      await updateMutation.mutateAsync(submitData);
    } else {
      await createMutation.mutateAsync(submitData);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="card max-w-md w-full mx-4 p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">
            {section ? 'Edit Section' : 'Add New Section'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Close modal"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {errors.submit && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
            {errors.submit}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="section-name" className="block text-sm font-medium text-gray-700 mb-1">
              Section Name *
            </label>
            <input
              id="section-name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className={`input w-full ${errors.name ? 'border-red-500' : ''}`}
              placeholder="e.g., Main Dining, Patio, Private Room"
            />
            {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
          </div>

          <div>
            <label htmlFor="section-duration" className="block text-sm font-medium text-gray-700 mb-1">
              Default Duration (minutes)
            </label>
            <input
              id="section-duration"
              type="number"
              min="1"
              value={formData.defaultDurationMinutes}
              onChange={(e) =>
                setFormData({ ...formData, defaultDurationMinutes: parseInt(e.target.value) || 60 })
              }
              className="input w-full"
            />
          </div>

          <div>
            <label htmlFor="section-sort-order" className="block text-sm font-medium text-gray-700 mb-1">
              Sort Order
            </label>
            <input
              id="section-sort-order"
              type="number"
              min="0"
              value={formData.sortOrder}
              onChange={(e) =>
                setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })
              }
              className="input w-full"
            />
          </div>

          <div>
            <label htmlFor="section-color" className="block text-sm font-medium text-gray-700 mb-1">
              Color
            </label>
            <input
              id="section-color"
              type="text"
              value={formData.color}
              onChange={(e) => setFormData({ ...formData, color: e.target.value })}
              className="input w-full"
              placeholder="e.g., #FF5733 or red"
            />
          </div>

          <div>
            <label htmlFor="section-max-guests" className="block text-sm font-medium text-gray-700 mb-1">
              Max Guests
            </label>
            <input
              id="section-max-guests"
              type="number"
              min="1"
              value={formData.maxGuests}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  maxGuests: e.target.value ? parseInt(e.target.value) : '',
                })
              }
              className="input w-full"
              placeholder="Leave blank for unlimited"
            />
          </div>

          <div>
            <label htmlFor="section-max-bookings" className="block text-sm font-medium text-gray-700 mb-1">
              Max Bookings per Slot
            </label>
            <input
              id="section-max-bookings"
              type="number"
              min="1"
              value={formData.maxBookingsPerSlot}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  maxBookingsPerSlot: e.target.value ? parseInt(e.target.value) : '',
                })
              }
              className="input w-full"
              placeholder="Leave blank for unlimited"
            />
          </div>

          <div>
            <label htmlFor="section-last-booking" className="block text-sm font-medium text-gray-700 mb-1">
              Last Booking (minutes before close)
            </label>
            <input
              id="section-last-booking"
              type="number"
              min="0"
              value={formData.lastBookingMinutesBeforeClose}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  lastBookingMinutesBeforeClose: e.target.value ? parseInt(e.target.value) : '',
                })
              }
              className="input w-full"
              placeholder="Leave blank for no restriction"
            />
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="section-enabled"
              checked={formData.enabled}
              onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="section-enabled" className="ml-2 text-sm text-gray-700">
              Enabled
            </label>
          </div>

          <div className="flex space-x-3 pt-4 border-t border-gray-200">
            <button type="button" onClick={onClose} className="btn btn-outline flex-1 h-10">
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary flex-1 h-10"
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {createMutation.isPending || updateMutation.isPending
                ? 'Saving...'
                : section
                ? 'Update Section'
                : 'Create Section'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default SectionsTab;